import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { updateCheckoutSection } from "../store/checkout-section-store";
import { useState } from "react";
import { useMediaQuery } from "react-responsive";
import WWLogoColor from "../assets/logos/ww_logo_mod.svg?react";
import { ButtonLink } from "./ButtonLink";

const shoppingCartIcon = (
  <FontAwesomeIcon icon={faCartShopping} size="lg" className="text-white" />
);

// Same teal-then-gradient-sweep underline used on the Contact page's form
// fields (see FieldUnderline in Contact.jsx), retriggered here by
// group-hover instead of peer-focus: a solid teal line scales in from the
// left, while a gradient version of the same line scales in alongside it
// and then fades out after a beat, leaving the settled line looking like
// it was drawn in one sweep rather than a flat color snapping into place.
const NavUnderline = () => (
  <>
    <span
      aria-hidden="true"
      className="pointer-events-none absolute left-0 -bottom-1 h-0.5 w-full origin-left scale-x-0 bg-accent-teal transition-transform duration-400 ease-out motion-reduce:transition-none group-hover:scale-x-100"
    ></span>
    <span
      aria-hidden="true"
      className="pointer-events-none absolute left-0 -bottom-1 h-0.5 w-full origin-left scale-x-0 bg-gradient-to-r from-accent-teal to-secondary-regular motion-reduce:transition-none group-hover:scale-x-100 group-hover:opacity-0"
      style={{
        transitionProperty: "transform, opacity",
        transitionDuration: "400ms, 350ms",
        transitionTimingFunction: "ease-out, ease-out",
        transitionDelay: "0ms, 350ms",
      }}
    ></span>
  </>
);

const navItems = [
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

const MainNavigation = () => {
  const dispatch = useDispatch();
  const cartCount = useSelector((state) => state.cart.cartData.itemCount);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const isMobileNavScreen = useMediaQuery({ query: "(max-width: 976px)" });
  const navHeight = useSelector((state) => state.general.navHeight);

  const updatedCheckoutSectionHandler = () => {
    dispatch(updateCheckoutSection("cart"));
  };

  const mobileNavHandler = () => {
    if (isMobileNavScreen) setShowMobileNav((prev) => !prev);
  };

  return (
    <nav
      id="nav"
      className="sticky top-0 z-30 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] py-[6px] lg:flex lg:items-center"
    >
      <div className="container grid grid-cols-12 mx-auto">
        <ul className="col-span-10 col-start-2 sm:col-span-12 sm:col-start-1 flex items-center gap-4 sm:max-lg:mx-4 justify-between">
          {isMobileNavScreen && (
            <button
              type="button"
              aria-label={showMobileNav ? "Close Menu" : "Open Menu"}
              aria-expanded={showMobileNav}
              aria-controls="mobile-nav-panel"
              className="relative w-6 h-6 shrink-0 active:scale-90 transition-transform duration-150 ease-out"
              onClick={mobileNavHandler}
            >
              <span
                className={`absolute inset-0 flex flex-col justify-center gap-[6px] transition-all duration-150 ease-out ${showMobileNav ? "opacity-0 scale-75" : "opacity-100 scale-100"}`}
              >
                <span className="h-[2px] w-full rounded-full bg-primary-dark"></span>
                <span className="h-[2px] w-[70%] rounded-full bg-primary-dark"></span>
                <span className="h-[2px] w-full rounded-full bg-primary-dark"></span>
              </span>
              <span
                className={`absolute inset-0 flex items-center justify-center transition-all duration-150 ease-out ${showMobileNav ? "opacity-100 scale-100" : "opacity-0 scale-75"}`}
              >
                <FontAwesomeIcon
                  icon={faXmark}
                  className="text-primary-dark text-xl"
                />
              </span>
            </button>
          )}

          <li className={`w-[160px] ${isMobileNavScreen ? "" : "mr-4"}`}>
            <NavLink
              onClick={() => setShowMobileNav(false)}
              to="/"
              className={({ isActive, isPending }) =>
                isActive ? "active" : isPending ? "pending" : ""
              }
              end
            >
              <WWLogoColor alt="Women Wavemakers" />
            </NavLink>
          </li>

          {!isMobileNavScreen && (
            <menu className="flex flex-nowrap gap-4 font-sora font-light text-md text-gray-500">
              {navItems.map(({ to, label }) => (
                <li className="mr-4" key={to}>
                  <NavLink
                    onClick={mobileNavHandler}
                    to={to}
                    className={({ isActive, isPending }) =>
                      `group ${isActive ? "active" : isPending ? "pending" : ""}`
                    }
                  >
                    <span className="nav-item relative block ml-5 uppercase">
                      {label}
                      <NavUnderline />
                    </span>
                  </NavLink>
                </li>
              ))}
            </menu>
          )}

          <li
            className={`justify-self-end transition-opacity duration-200 ${showMobileNav ? "opacity-0" : "opacity-100"}`}
          >
            <NavLink
              aria-label="View cart and checkout"
              onClick={updatedCheckoutSectionHandler}
              to="checkout"
              className="relative inline-flex items-center active:scale-90 transition-transform duration-150 ease-out"
            >
              <span className="relative flex items-center justify-center w-10 h-10 rounded-full bg-primary-dark">
                {shoppingCartIcon}
                <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-secondary-regular text-primary-dark text-[14px] font-semibold leading-none">
                  {cartCount}
                </span>
              </span>
            </NavLink>
          </li>
        </ul>
      </div>

      {isMobileNavScreen && (
        <div
          id="mobile-nav-panel"
          className={`fixed left-0 right-0 bottom-0 z-20 bg-white overflow-y-auto transition-transform duration-300 ease-in-out ${showMobileNav ? "translate-x-0" : "translate-x-full"}`}
          style={{ top: navHeight ? `${navHeight}px` : undefined }}
        >
          <ul className="flex flex-col text-left px-6 pt-8 font-sora font-light text-2xl text-gray-500">
            {navItems.map(({ to, label }) => (
              <li className="" key={to}>
                <NavLink
                  onClick={mobileNavHandler}
                  to={to}
                  className={({ isActive, isPending }) =>
                    `group block py-4 uppercase active:scale-[0.97] transition-transform duration-150 ease-out ${isActive ? "active" : isPending ? "pending" : ""}`
                  }
                >
                  <span className="nav-item relative">
                    {label}
                    <NavUnderline />
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="px-6 py-8" onClick={mobileNavHandler}>
            <ButtonLink toLink="/shop" linkText="Shop New Arrivals" />
          </div>
        </div>
      )}
    </nav>
  );
};

export { MainNavigation };
